import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback } from 'react';
import { NewDropdown, Typography } from 'app/components/ui';
import { DocumentTaxonomyConfigContext, TagContextInfo, useFindOrCreateTagContext, useAddContextToConfig, useUpdateTag } from 'entities/tag';
import { AddDateModal } from 'features/tagging/Taxonomies/components/modals/AddDateModal';
import { i18n } from 'shared/i18n';

interface DateFieldProps {
  tagId: string;
  documentSuperId: number;
  selectedContextId: string | null;
  tagContext: TagContextInfo | null;
  contextSelections: DocumentTaxonomyConfigContext[];
  configId?: string;
  disabled?: boolean;
}

type DateInput = { type: 'period' | 'instant' | 'forever'; fromDate?: Date; toDate?: Date; date?: Date };

const formatDate = (dateValue: string | Date) => {
  return new Date(dateValue).toLocaleDateString('sv-SE');
};

const formatContextLabel = (context: {
  periodForever?: boolean;
  periodInstant?: Date | string | null;
  periodStartDate?: Date | string | null;
  periodEndDate?: Date | string | null;
}): string => {
  if (context.periodForever) return i18n('tag:forever.label');
  if (context.periodInstant) return `${i18n('tag:instant.label')}: ${formatDate(context.periodInstant)}`;
  if (context.periodStartDate && context.periodEndDate) {
    return `${i18n('tag:period.title')}: ${formatDate(context.periodStartDate)} \u2192 ${formatDate(context.periodEndDate)}`;
  }
  return '';
};

const getContextLabel = (ctx: DocumentTaxonomyConfigContext): string => {
  const context = ctx.context;
  if (!context) return '';
  return formatContextLabel(context);
};

export const DateField = ({ tagId, documentSuperId, selectedContextId, tagContext, contextSelections, configId, disabled = false }: DateFieldProps) => {
  const { t } = useTranslation('tag');

  const [showModal, setShowModal] = useState(false);

  const { mutate: updateTag } = useUpdateTag();
  const { mutateAsync: findOrCreateTagContext } = useFindOrCreateTagContext();
  const { mutateAsync: addContextToConfig } = useAddContextToConfig();

  const hasForeverDate = useMemo(() => {
    return contextSelections.some((ctx) => ctx.context?.periodForever);
  }, [contextSelections]);

  const allContextOptions = useMemo(() => {
    const options = contextSelections.map((cs) => ({
      value: cs.contextId,
      label: getContextLabel(cs),
    }));

    // If the tag's context is not in config selections, add it so the dropdown can display it
    if (tagContext && selectedContextId && !options.some((o) => o.value === selectedContextId)) {
      options.push({ value: selectedContextId, label: formatContextLabel(tagContext) });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [contextSelections, tagContext, selectedContextId]);

  const handleContextSelect = useCallback(
    (contextId: string) => {
      updateTag({ id: tagId, contextId, documentSuperId });
    },
    [tagId, documentSuperId, updateTag]
  );

  const handleCustomDateAdd = useCallback(
    async (dateData: DateInput) => {
      if (!configId) return;

      try {
        const context = await findOrCreateTagContext({
          documentSuperId,
          periodStartDate: dateData.type === 'period' ? dateData.fromDate?.toISOString() ?? null : null,
          periodEndDate: dateData.type === 'period' ? dateData.toDate?.toISOString() ?? null : null,
          periodInstant: dateData.type === 'instant' ? dateData.date?.toISOString() ?? null : null,
          periodForever: dateData.type === 'forever',
        });

        const alreadyInConfig = contextSelections.some((cs) => cs.contextId === context.id);
        if (!alreadyInConfig) {
          await addContextToConfig({ configId, contextId: context.id });
        }

        handleContextSelect(context.id);
      } catch (error) {
        console.error('Failed to create custom date:', error);
      }
    },
    [configId, documentSuperId, contextSelections, findOrCreateTagContext, addContextToConfig, handleContextSelect]
  );

  if (disabled) {
    const currentLabel = allContextOptions.find((o) => o.value === selectedContextId)?.label || t('date.choose');
    return (
      <Typography variant="body-7" fontWeight="light">
        {currentLabel}
      </Typography>
    );
  }

  return (
    <>
      <NewDropdown
        fullwidth
        options={allContextOptions}
        currentValue={selectedContextId || t('date.choose')}
        onUpdate={handleContextSelect}
        customOption={
          <button onClick={() => setShowModal(true)} className="flex-row custom-option-btn">
            <i className="fa-regular fa-circle-plus" />
            <Typography variant="body-5" fontWeight="light" color="#1E2533">
              {t('create.custom.value')}
            </Typography>
          </button>
        }
      />

      <AddDateModal show={showModal} hasForeverDate={hasForeverDate} onClose={() => setShowModal(false)} onAdd={handleCustomDateAdd} />
    </>
  );
};
